/**
 * Admin API Client
 * Handles all API requests to the admin backend
 */

import type {
  ActivityLogEntry,
  AdminSession,
  AdminUser,
  ApiResponse,
  Currency,
  DashboardOverview,
  Organization,
  OrganizationDetails,
  PaginatedResponse,
  Pair,
} from "../types"

// API base URL - use env variable for local, replace admin with api for dev/prod
const API_BASE_URL = import.meta.env.VITE_API_URL || window.location.origin.replace("admin.", "api.")

class ApiClient {
  private token: string | null = null

  constructor() {
    // Load token from localStorage on init
    this.token = localStorage.getItem("admin_token")
  }

  setToken(token: string) {
    this.token = token
    localStorage.setItem("admin_token", token)
  }

  clearToken() {
    this.token = null
    localStorage.removeItem("admin_token")
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      ...(options.headers as Record<string, string>),
    }

    if (this.token) {
      headers.Authorization = `Bearer ${this.token}`
    }

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers,
    })

    const data = await response.json()

    if (!response.ok) {
      throw new Error(data.message || "API request failed")
    }

    return data
  }

  // Auth endpoints
  async login(email: string, password: string): Promise<AdminSession> {
    const response = await this.request<AdminSession>("/admin/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    })

    if (response.data) {
      this.setToken(response.data.sessionToken)
      return response.data
    }

    throw new Error("Login failed")
  }

  async logout(): Promise<void> {
    await this.request("/admin/auth/logout", { method: "POST" })
    this.clearToken()
  }

  async getMe(): Promise<{ username: string; email: string; role: string }> {
    const response = await this.request<{ username: string; email: string; role: string }>("/admin/auth/me")
    if (!response.data) throw new Error("Failed to get user data")
    return response.data
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await this.request("/admin/auth/change-password", {
      method: "POST",
      body: JSON.stringify({ currentPassword, newPassword }),
    })
  }

  // Organizations endpoints
  async listOrganizations(params?: {
    page?: number
    limit?: number
    search?: string
    status?: string
  }): Promise<PaginatedResponse<Organization>> {
    const query = new URLSearchParams()
    if (params?.page) query.append("page", params.page.toString())
    if (params?.limit) query.append("limit", params.limit.toString())
    if (params?.search) query.append("search", params.search)
    if (params?.status) query.append("status", params.status)

    const response = await this.request<PaginatedResponse<Organization>>(`/admin/organizations?${query.toString()}`)
    if (!response.data) throw new Error("Failed to get organizations")
    return response.data
  }

  async getOrganization(orgId: string): Promise<OrganizationDetails> {
    const response = await this.request<OrganizationDetails>(`/admin/organizations/${orgId}`)
    if (!response.data) throw new Error("Failed to get organization")
    return response.data
  }

  async createOrganization(data: {
    name: string
    displayName?: string
    billingEmail: string
    entityAssociated: string
    creditLimit?: number
    isTradingAllowed?: boolean
    notes?: string
    firstName: string
    lastName: string
    email: string
    password?: string
  }): Promise<Organization> {
    const response = await this.request<{ organization: Organization }>("/admin/organizations", {
      method: "POST",
      body: JSON.stringify(data),
    })
    if (!response.data?.organization) throw new Error("Failed to create organization")
    return response.data.organization
  }

  async updateOrganization(
    orgId: string,
    data: {
      creditLimit?: number
      status?: "active" | "inactive" | "suspended"
      notes?: string
      isTradingAllowed?: boolean
    },
  ): Promise<Organization> {
    const response = await this.request<{ organization: Organization }>(`/admin/organizations/${orgId}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    })
    if (!response.data?.organization) throw new Error("Failed to update organization")
    return response.data.organization
  }

  async addUserToOrg(
    orgId: string,
    data: {
      firstName: string
      lastName: string
      email: string
      roles: string[]
      password?: string | null
    },
  ): Promise<{
    id: string
    username: string
    email: string
    firstName: string
    lastName: string
    roles: string[]
    password: string
  }> {
    const response = await this.request<{
      user: {
        id: string
        username: string
        email: string
        firstName: string
        lastName: string
        roles: string[]
        password: string
      }
    }>(`/admin/organizations/${orgId}/users`, {
      method: "POST",
      body: JSON.stringify(data),
    })
    if (!response.data?.user) throw new Error("Failed to add user")
    return response.data.user
  }

  async updateUserInOrg(
    orgId: string,
    userId: string,
    data: {
      firstName?: string
      lastName?: string
      email?: string
      status?: "active" | "inactive"
      isTradingAllowed?: boolean
    },
  ): Promise<{ id: string; email: string; firstName: string; lastName: string }> {
    const response = await this.request<{
      user: { id: string; email: string; firstName: string; lastName: string }
    }>(`/admin/organizations/${orgId}/users/${userId}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    })
    if (!response.data?.user) throw new Error("Failed to update user")
    return response.data.user
  }

  async resetUserPassword(
    orgId: string,
    userId: string,
    newPassword: string,
  ): Promise<{ userId: string; newPassword: string }> {
    const response = await this.request<{ userId: string; newPassword: string }>(
      `/admin/organizations/${orgId}/users/${userId}/reset-password`,
      {
        method: "POST",
        body: JSON.stringify({ newPassword }),
      },
    )
    if (!response.data) throw new Error("Failed to reset password")
    return response.data
  }

  async removeUserFromOrg(orgId: string, userId: string): Promise<void> {
    await this.request(`/admin/organizations/${orgId}/users/${userId}`, {
      method: "DELETE",
    })
  }

  async createApiKey(
    orgId: string,
    data: {
      nickname: string
      scope: "trade:read" | "trade:write"
      principalId?: string
    },
  ): Promise<{
    id: string
    nickname: string
    publicKey: string
    privateKey: string
    scope: string
    status: string
    createdAt: string
  }> {
    const response = await this.request<{
      apiKey: {
        id: string
        nickname: string
        publicKey: string
        privateKey: string
        scope: string
        status: string
        createdAt: string
      }
    }>(`/admin/organizations/${orgId}/api-keys`, {
      method: "POST",
      body: JSON.stringify(data),
    })
    if (!response.data?.apiKey) throw new Error("Failed to create API key")
    return response.data.apiKey
  }

  async revokeApiKey(orgId: string, keyId: string): Promise<void> {
    await this.request(`/admin/organizations/${orgId}/api-keys/${keyId}/revoke`, {
      method: "PATCH",
    })
  }

  async shareApiKey(
    orgId: string,
    keyId: string,
    data: {
      userIds: string[]
      publicKey: string
      privateKey: string
    },
  ): Promise<{
    message: string
    details: Array<{
      email: string
      success: boolean
      error?: string
    }>
  }> {
    const response = await this.request<{
      message: string
      details: Array<{
        email: string
        success: boolean
        error?: string
      }>
    }>(`/admin/organizations/${orgId}/api-keys/${keyId}/share`, {
      method: "POST",
      body: JSON.stringify(data),
    })
    if (!response.data) throw new Error("Failed to share API key")
    return response.data
  }
  async listNewCurrenciesForOrg(orgId: string): Promise<{ data: { currencies: Currency[] } }> {
    const response = await this.request<{
      currencies: Currency[]
    }>(`/admin/organizations/${orgId}/pending-currencies`)
    if (!response.data) throw new Error("Failed to get new currencies for org")
    return {
      data: { currencies: response.data.currencies },
    }
  }

  async addBalanceToOrg(
    orgId: string,
    data: {
      currencyId: string
    },
  ): Promise<{
    id: string
    currency: string
    amount: number
    amountNumeric: string
    currencyType: string
    enabled: boolean
    createdAt: string
    updatedAt: string
    deletedAt: string | null
    orgId: string
  }> {
    const response = await this.request<{
      balance: {
        id: string
        currency: string
        amount: number
        amountNumeric: string
        currencyType: string
        enabled: boolean
        createdAt: string
        updatedAt: string
        deletedAt: string | null
        orgId: string
      }
    }>(`/admin/organizations/${orgId}/balances`, {
      method: "POST",
      body: JSON.stringify(data),
    })
    if (!response.data?.balance) throw new Error("Failed to add currency balance")
    return response.data.balance
  }

  // Analytics endpoints
  async getDashboardOverview(): Promise<DashboardOverview> {
    const response = await this.request<DashboardOverview>("/admin/analytics/overview")
    if (!response.data) throw new Error("Failed to get dashboard overview")
    return response.data
  }

  async getActivityLog(params?: {
    page?: number
    limit?: number
  }): Promise<{ data: ActivityLogEntry[]; pagination: { page: number; limit: number } }> {
    const query = new URLSearchParams()
    if (params?.page) query.append("page", params.page.toString())
    if (params?.limit) query.append("limit", params.limit.toString())

    const response = await this.request<{
      logs: ActivityLogEntry[]
      pagination: { page: number; limit: number }
    }>(`/admin/analytics/activity-log?${query.toString()}`)

    if (!response.data) throw new Error("Failed to get activity log")
    return {
      data: response.data.logs,
      pagination: response.data.pagination,
    }
  }
  async listCurrencies(params?: {
    page?: number
    limit?: number
    sortBy?: "symbol" | "name"
    sortOrder?: "asc" | "desc"
    all?: boolean
  }): Promise<PaginatedResponse<Currency>> {
    const query = new URLSearchParams()
    if (params?.page !== undefined) query.append("page", params.page.toString())
    if (params?.limit !== undefined) query.append("limit", params.limit.toString())
    if (params?.sortBy) query.append("sortBy", params.sortBy)
    if (params?.sortOrder) query.append("sortOrder", params.sortOrder)
    if (params?.all !== undefined) query.append("all", params.all.toString())

    const response = await this.request<{
      currencies: Currency[]
      pagination: PaginatedResponse<Currency>["pagination"]
    }>(`/admin/platform/currencies?${query.toString()}`)
    if (!response.data) throw new Error("Failed to get currencies")
    return {
      data: response.data.currencies,
      pagination: response.data.pagination,
    }
  }

  async listPairs(params?: {
    page?: number
    limit?: number
    sortBy?: "pair"
    sortOrder?: "asc" | "desc"
  }): Promise<PaginatedResponse<Pair>> {
    const query = new URLSearchParams()
    if (params?.page) query.append("page", params.page.toString())
    if (params?.limit) query.append("limit", params.limit.toString())
    if (params?.sortBy) query.append("sortBy", params.sortBy)
    if (params?.sortOrder) query.append("sortOrder", params.sortOrder)

    const response = await this.request<{
      pairs: Pair[]
      pagination: PaginatedResponse<Pair>["pagination"]
    }>(`/admin/platform/pairs?${query.toString()}`)
    if (!response.data) throw new Error("Failed to get pairs")
    return {
      data: response.data.pairs,
      pagination: response.data.pagination,
    }
  }

  async addCurrency(data: {
    name: string
    symbol: string
    currencyType: "FIAT" | "CRYPTO"
  }): Promise<Currency> {
    const response = await this.request<Currency>("/admin/platform/currencies", {
      method: "POST",
      body: JSON.stringify(data),
    })
    if (!response.data) throw new Error("Failed to create currency")
    return response.data
  }

  async updateCurrency(
    currencyId: string,
    data: {
      valueInUSD?: number
      enabled?: boolean
      min?: number
      max?: number
    },
  ): Promise<Currency> {
    const response = await this.request<{ currency: Currency }>("/admin/platform/currencies", {
      method: "PATCH",
      body: JSON.stringify({ currencyId, ...data }),
    })
    if (!response.data?.currency) throw new Error("Failed to update currency")
    return response.data.currency
  }

  async updatePair(
    pairId: string,
    data: {
      enabled?: boolean
    },
  ): Promise<Pair> {
    const response = await this.request<{ pair: Pair }>("/admin/platform/pairs", {
      method: "PATCH",
      body: JSON.stringify({ pairId, ...data }),
    })
    if (!response.data?.pair) throw new Error("Failed to update pair")
    return response.data.pair
  }

  // Admin users endpoints
  async listAdminUsers(params?: {
    page?: number
    limit?: number
    sortBy?: "createdAt" | "email" | "username" | "lastLogin"
    sortOrder?: "asc" | "desc"
  }): Promise<PaginatedResponse<AdminUser>> {
    const query = new URLSearchParams()
    if (params?.page) query.append("page", params.page.toString())
    if (params?.limit) query.append("limit", params.limit.toString())
    if (params?.sortBy) query.append("sortBy", params.sortBy)
    if (params?.sortOrder) query.append("sortOrder", params.sortOrder)

    const response = await this.request<{
      adminUsers: AdminUser[]
      pagination: PaginatedResponse<AdminUser>["pagination"]
    }>(`/admin/admin-users?${query.toString()}`)
    if (!response.data) throw new Error("Failed to get admin users")
    return {
      data: response.data.adminUsers,
      pagination: response.data.pagination,
    }
  }

  async createAdminUser(data: {
    username: string
    email: string
    password: string
    role: "super_admin" | "admin" | "viewer"
  }): Promise<AdminUser> {
    const response = await this.request<{ adminUser: AdminUser }>("/admin/admin-users", {
      method: "POST",
      body: JSON.stringify(data),
    })
    if (!response.data?.adminUser) throw new Error("Failed to create admin user")
    return response.data.adminUser
  }

  async updateAdminUser(
    userId: string,
    data: {
      role?: "super_admin" | "admin" | "viewer"
      status?: "active" | "inactive"
      isActive?: boolean
    },
  ): Promise<AdminUser> {
    const response = await this.request<{ adminUser: AdminUser }>(`/admin/admin-users/${userId}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    })
    if (!response.data?.adminUser) throw new Error("Failed to update admin user")
    return response.data.adminUser
  }

  async deleteAdminUser(userId: string): Promise<void> {
    await this.request(`/admin/admin-users/${userId}`, {
      method: "DELETE",
    })
  }

  async resetAdminPassword(
    adminUserId: string,
    newPassword: string,
  ): Promise<{ adminUserId: string; username: string; email: string }> {
    const response = await this.request<{ adminUserId: string; username: string; email: string }>(
      `/admin/admin-users/${adminUserId}/reset-password`,
      {
        method: "POST",
        body: JSON.stringify({ newPassword }),
      },
    )
    if (!response.data) throw new Error("Failed to reset admin password")
    return response.data
  }
}

export const apiClient = new ApiClient()
